import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  Select,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  InputAdornment,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert as MuiAlert,
  Divider,
  Popover,
  Checkbox,
} from "@mui/material";
import {
  CheckCircle as CheckCircleIcon,
  FirstPage,
  LastPage,
  ChevronLeft,
  ChevronRight,
  CheckBoxOutlineBlank as CheckBoxUncheckedIcon,
  CheckBox as CheckBoxCheckedIcon,
  IndeterminateCheckBox as IndeterminateIcon,
} from "@mui/icons-material";
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from "@mui/icons-material/Cancel";
import { useLocation } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import { EmailApiService } from "@/services";
import PaginationButtons from "@/components/PaginationButtons";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import EditIcon from '@mui/icons-material/Edit';
import SendIcon from '@mui/icons-material/Send';
import axios from "axios";
import EmailDetailsViewer from "@/components/EmailDetailsViewer";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { API_CONFIG, API_ENDPOINTS } from "../constants/apiConfig";
import * as XLSX from "xlsx";

interface Email {
  id: string | number;
  customer_name?: string;
  category?: string;
  from_email?: string;
  subject?: string;
  summary?: string;
  received_utc?: string;
  status?: EmailStatusType;
  classification?: string;
  created_at?: string;
  process_time?: string;
  Status?: string;
  extension?: string;
  attachments?: any[];
  extraction?: {
    count?: number;
    input_file_count?: number;
    status?: string;
    [key: string]: any;
  } | null;
}

interface EmailListResponses {
  data: Email[];
  pagination?: {
    total_records: number;
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  message: string;
  success: boolean;
  today_completed_count?: number;
  today_error_count?: number;
  today_processed_count?: number;
  today_processing_count?: number;
}

/**
 * Represents the status of an email in the system
 * @typedef {'pending' | 'accepted' | 'rejected'} EmailStatus
 */
type EmailStatusType =
  | "pending"
  | "accepted"
  | "rejected"
  | "Accepted"
  | "Rejected"
  | null;

/**
 * Email Attachments Page Component
 *
 * Displays a paginated list of email attachments with filtering, sorting, and action capabilities.
 * Provides functionality to view, accept, or reject email attachments with optional notes.
 *
 * @component
 * @returns {JSX.Element} The email attachments page component
 *
 * @example
 * <Route path="/email-attachments" element={<EmailAttachmentsPage />} />
 *
 * @description
 * This component handles:
 * - Fetching email data with pagination
 * - Client-side search and filtering
 * - Email status management (accept/reject)
 * - Error handling and loading states
 * - Responsive layout for different screen sizes
 */
// Helper function to get the no results message
const getNoResultsMessage = () => "No emails found";

const EmailAttachmentsPage: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();

  // Custom hook to handle location state updates
  const useLocationState = () => {
    const [page, setPage] = useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchQuery, setSearchQuery] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<string | string[]>("");
    const [dateRange, setDateRange] = useState<{
      startDate: Date | null;
      endDate: Date | null;
    }>({ startDate: null, endDate: null });
    const locationStateRef = React.useRef(location.state);

    // Memoize the state setters to prevent unnecessary re-renders
    const stableSetPage = React.useCallback((value: React.SetStateAction<number>) => {
      setPage(value);
    }, []);

    const stableSetRowsPerPage = React.useCallback((value: React.SetStateAction<number>) => {
      setRowsPerPage(value);
    }, []);

    const stableSetSearchQuery = React.useCallback((value: React.SetStateAction<string>) => {
      setSearchQuery(value);
    }, []);

    const stableSetCategoryFilter = React.useCallback((value: React.SetStateAction<string | string[]>) => {
      setCategoryFilter(value);
    }, []);

    const stableSetDateRange = React.useCallback((value: React.SetStateAction<{startDate: Date | null, endDate: Date | null}>) => {
      setDateRange(value);
    }, []);

    // This effect only runs when the component mounts or location.state changes
    useEffect(() => {
      // Skip if location.state hasn't changed or is null
      if (location.state === locationStateRef.current) return;
      
      locationStateRef.current = location.state;
      
      if (!location.state) return;

      const {
        page: statePage,
        rowsPerPage: stateRowsPerPage,
        searchQuery: stateSearchQuery,
        categoryFilter: stateCategoryFilter,
        dateRange: stateDateRange,
      } = location.state;

      // Update states only if they've changed
      if (statePage !== undefined) {
        stableSetPage(statePage);
      }
      if (stateRowsPerPage !== undefined) {
        stableSetRowsPerPage(stateRowsPerPage);
      }
      if (stateSearchQuery !== undefined) {
        stableSetSearchQuery(stateSearchQuery);
        setFilters(prev => ({ ...prev, searchQuery: stateSearchQuery }));
      }
      if (stateCategoryFilter !== undefined) {
        stableSetCategoryFilter(stateCategoryFilter);
        const normalizedCategories = Array.isArray(stateCategoryFilter) 
          ? stateCategoryFilter 
          : (stateCategoryFilter ? [stateCategoryFilter] : []);
        setFilters(prev => ({ ...prev, category: normalizedCategories }));
      }
      if (stateDateRange !== undefined) {
        const newDateRange = {
          startDate: stateDateRange.startDate ? new Date(stateDateRange.startDate) : null,
          endDate: stateDateRange.endDate ? new Date(stateDateRange.endDate) : null,
        };
        stableSetDateRange(newDateRange);
        setFilters(prev => ({
          ...prev,
          startDate: newDateRange.startDate,
          endDate: newDateRange.endDate,
        }));
      }

      // Clear the location state to prevent reapplication on refresh
      window.history.replaceState({}, document.title);
    }, [stableSetPage, stableSetRowsPerPage, stableSetSearchQuery, stableSetCategoryFilter, stableSetDateRange]);

    return {
      page,
      setPage: stableSetPage,
      rowsPerPage,
      setRowsPerPage: stableSetRowsPerPage,
      searchQuery,
      setSearchQuery: stableSetSearchQuery,
      categoryFilter,
      setCategoryFilter: stableSetCategoryFilter,
      dateRange,
      setDateRange: stableSetDateRange,
    };
  };

  // Initialize state with custom hook
  const { page, setPage, rowsPerPage, setRowsPerPage, searchQuery, setSearchQuery, categoryFilter: categoryFilterFromState, setCategoryFilter, dateRange, setDateRange } = useLocationState();
  
  // Ensure categoryFilter is always an array of strings
  const categoryFilter = React.useMemo(() => {
    if (!categoryFilterFromState) return [];
    return Array.isArray(categoryFilterFromState) 
      ? categoryFilterFromState 
      : [categoryFilterFromState];
  }, [categoryFilterFromState]);

  /**
   * State for pagination and search functionality
   * @type {Object}
   * @property {number} page - Current page number (1-based)
   * @property {number} rowsPerPage - Number of rows to display per page
   * @property {string} searchQuery - Current search query string
   * @property {NodeJS.Timeout | null} filterTimeout - Timeout reference for search debouncing
   */

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      searchQuery: "",
      category: [],
      startDate: null,
      endDate: null,
    });
    setSearchQuery("");
    setCategoryFilter([]);
    setDateRange({ startDate: null, endDate: null });
    setHasExtensionFilter(false);
    setPage(1);
  };

  /**
   * State for managing emails data and API responses
   * @type {Object}
   * @property {Array<Email & { status?: EmailStatus }>} emails - List of emails with optional status
   * @property {boolean} loading - Loading state indicator
   * @property {string | null} error - Error message if API request fails
   * @property {EmailListResponses} emailResponse - Full response from the emails API
   */
  const [emails, setEmails] = useState<
    Array<Email & { status?: EmailStatusType }>
  >([]);
  const [selectedEmail, setSelectedEmail] = useState<
    (Email & { status?: EmailStatusType }) | null
  >(null);
  const [emailDetails, setEmailDetails] = useState<any>(null);
  const [actionType, setActionType] = useState<"accepted" | "rejected" | null>(
    null
  );
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [inlineAlert, setInlineAlert] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'success' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });

  // Auto-close alert after 5 seconds
  useEffect(() => {
    if (inlineAlert.open) {
      const timer = setTimeout(() => {
        setInlineAlert({ ...inlineAlert, open: false });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [inlineAlert.open]);
  // Handle date range change
  const [filters, setFilters] = useState<{
    searchQuery: string;
    category: string[];
    startDate: Date | null;
    endDate: Date | null;
  }>({
    searchQuery: "",
    category: [],
    startDate: null,
    endDate: null,
  });
  const [filterAnchor, setFilterAnchor] = useState<HTMLButtonElement | null>(null);
  const [hasExtensionFilter, setHasExtensionFilter] = useState(false);
  const [emailResponse, setEmailResponse] = useState<EmailListResponses>({
    data: [],
    pagination: {
      total_records: 0,
      page: 1,
      limit: rowsPerPage,
      total: 0,
      totalPages: 0,
    },
    message: "",
    success: false,
    today_completed_count: 0,
    today_error_count: 0,
    today_processed_count: 0,
    today_processing_count: 0,
  });

  // Auto-apply search query with a small debounce
  useEffect(() => {
    if (filters.searchQuery === searchQuery) return;

    const timer = setTimeout(() => {
      setPage(1);
      setSearchQuery(filters.searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [filters.searchQuery, searchQuery, setPage, setSearchQuery]);

  /**
   * Effect hook to handle initial load and pagination/search changes
   * @effect
   * @dependencies location.state, page, searchQuery, rowsPerPage
   * @description
   * - Syncs component state with URL state on initial load
   - Handles back/forward navigation with preserved state
   - Triggers data fetching when pagination or search changes
   */
  // Update the existing useEffect that handles location.state
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerEmail, setDrawerEmail] = useState<string | number | undefined>(undefined);
  const [isDrawerActive, setIsDrawerActive] = useState(false);

  const openDrawerForEmail = (emailId: string | number | undefined) => {
    setDrawerEmail(emailId);
    setDrawerOpen(true);
    setIsDrawerActive(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setDrawerEmail(undefined);
    setIsDrawerActive(false);
  };
  const fetchEmailDetails = React.useCallback(async () => {
    try {
      setError("");

      if (!drawerEmail) {
        setError("No email ID provided");
        return;
      }

      const emailService = new EmailApiService();
      const response = await emailService.getEmailById(drawerEmail, true);

      // Extract single email data from response
      const emailData = EmailApiService.extractSingleEmailData(response);

      // Ensure we have valid email data
      if (!emailData || typeof emailData !== "object") {
        throw new Error("Invalid email data received from API");
      }

      setEmailDetails(emailData);
    } catch (err: any) {
      let errorMessage = "Failed to fetch email details";

      if (axios.isAxiosError(err)) {
        if (err.response?.status === 404) {
          errorMessage = "Email not found. Please check the email ID.";
        } else if (err.response?.status === 500) {
          errorMessage = "Server error occurred while fetching email details.";
        } else if (err.response?.status === 0 || err.code === "ERR_NETWORK") {
          errorMessage = "CORS Error: Unable to connect to the API.";
        } else if (err.response?.data) {
          errorMessage = `API Error: ${
            err.response.data.message ||
            err.response.data.error ||
            "Unknown error"
          }`;
        }
      }

      setError(errorMessage);
    }
  }, [drawerEmail]);

  /**
   * Effect hook to load email data when drawerEmail changes
   * @effect
   * @dependencies [drawerEmail]
   * @description
   * - Triggers email data fetching when drawerEmail changes
   * - Only runs when drawerEmail has a value
   */
  useEffect(() => {
    if (drawerEmail) {
      fetchEmailDetails();
    }
  }, [drawerEmail, fetchEmailDetails]);

  // Effect for managing drawer styles
  useEffect(() => {
    if (isDrawerActive) {
      document.body.classList.add("pdf-view-active");
      const styleEl = document.createElement("style");
      styleEl.id = "drawer-split-layout";
      styleEl.textContent = `
        body.pdf-view-active .main-content-area {
          transition: all 0.3s ease-in-out !important;
          width: 56% !important;
    display: inline-block !important;
    vertical-align: top !important;
    position: fixed;
    left: 0%;
    height:'calc(100vh - 100px)',
    overflow:'scroll',
    margin-bottom:10px;
        }
        body.pdf-view-active .right-side-panel {
          position: fixed !important;
          right: 0 !important;
          top: 66px !important;
          bottom: 0 !important;
          width: 45% !important;
          background:#b8b4f129 !important;
          border-left: 1px solid #e0e0e0 !important;
          box-shadow: -2px 0 10px rgba(0,0,0,0.1) !important;
          // border-radius: 8px 0 0 8px !important;
          z-index: 1300 !important;
          overflow: hidden !important;
          transition: transform 0.3s ease-in-out !important;
          transform: translateX(0) !important;
           height:'calc(100vh - 100px)';
           overflow:'scroll';
           margin-bottom:10px;
        }
         /* 1000px to 1400px — medium desktop */
  @media (min-width: 800px) and (max-width: 1000px) {
  body.pdf-view-active .main-content-area {
      width: 50% !important;
      left: 0% !important;
    }
      body.pdf-view-active .right-side-panel {
      width:48% !important;
      }
  }
  @media (min-width: 1000px) and (max-width: 1399px) {
    body.pdf-view-active .main-content-area {
      width: 56% !important;
      left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 44% !important;
    }
  }

  /* 1400px to 1600px — large desktop */
  @media (min-width: 1400px) and (max-width: 1599px) {
    body.pdf-view-active .main-content-area {
      width: 60% !important;
      left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 41% !important;
    }
  }

  /* 1600px to 1920px — extra large desktop */
  @media (min-width: 1600px) and (max-width: 1919px) {
    body.pdf-view-active .main-content-area {
      width: 57% !important;
      left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 38% !important;
    }
  }

  /* 1920px and above — ultra wide / 4K */
  @media (min-width: 1920px) and (max-width: 2100px) {
    body.pdf-view-active .main-content-area {
      width: 61% !important;
        left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 35% !important;
    }
  }
  @media (min-width: 2300px) and (max-width: 2500px) {
   body.pdf-view-active .main-content-area {
      width: 66% !important;
      left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 35% !important;
    }
  }
      @media (min-width: 2500px) and (max-width: 3000px) {
   body.pdf-view-active .main-content-area {
      width: 68% !important;
      left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 35% !important;
    }
  }
      @media (min-width: 3000px) and (max-width: 4500px) {
   body.pdf-view-active .main-content-area {
      width: 78% !important;
        left: 0% !important;
    }
    body.pdf-view-active .right-side-panel {
      width: 35% !important;
    }
  }
  /* Mobile / tablet adjustments (below 768px) */
  @media (max-width: 768px) {
    body.pdf-view-active .main-content-area,
    body.pdf-view-active .right-side-panel {
      width: 100% !important;
      position: static !important;
      height: auto !important;
      transform: none !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      border-left: none !important;
      left: auto !important;
      right: auto !important;
      overflow: visible !important;
      display: block !important;
    }
  }
      `;
      document.head.appendChild(styleEl);
    } else {
      document.body.classList.remove("pdf-view-active");
      document.getElementById("drawer-split-layout")?.remove();
    }

    return () => {
      document.body.classList.remove("pdf-view-active");
      document.getElementById("drawer-split-layout")?.remove();
    };
  }, [isDrawerActive]);

  const handleActionClick = (email: Email, action: "accepted" | "rejected") => {
    setSelectedEmail(email);
    setActionType(action);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<{ value: unknown }>
  ) => {
    setRowsPerPage(Number(event.target.value));
    setPage(1);
  };

  const handleFirstPage = () => {
    setPage(1);
  };

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setPage((prev) =>
      Math.min(
        prev + 1,
        Math.ceil((emailResponse.pagination?.total_records || 0) / rowsPerPage)
      )
    );
  };

  const handleLastPage = () => {
    setPage(
      Math.ceil((emailResponse.pagination?.total_records || 0) / rowsPerPage)
    );
  };

  const handlePageNumberClick = (pageNumber: number) => {
    setPage(pageNumber);
  };

  // Add snackbar state and handler
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const applyFilters = () => {
    setSearchQuery(filters.searchQuery || "");
    setCategoryFilter(filters.category || "");
    setDateRange({
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    setPage(1); // Reset to first page when filters change
    setFilterAnchor(null);
  };



// Effect to handle static data loading
useEffect(() => {
 const loadStaticData = async () => {
  try {
    setLoading(true);
    setError(null);

    const emailService = new EmailApiService();

    const response = await emailService.getEmails(false, {
      page,
      rowsPerPage,
      searchQuery,
      category: Array.isArray(categoryFilter)
        ? categoryFilter.join(",")
        : categoryFilter,
      startDate: dateRange.startDate
        ? dateRange.startDate.toISOString().split("T")[0]
        : undefined,
      endDate: dateRange.endDate
        ? dateRange.endDate.toISOString().split("T")[0]
        : undefined,
    });

    console.log("Email API Response", response);

    // response.data contains API response
    setEmails(response.data.data || []);

    setEmailResponse({
      data: response.data.data || [],
      pagination: response.data.pagination,
      message: response.data.message,
      success: response.data.success,
      today_completed_count: response.data.today_completed_count ?? 0,
      today_error_count: response.data.today_error_count ?? 0,
      today_processed_count: response.data.today_processed_count ?? 0,
      today_processing_count: response.data.today_processing_count ?? 0,
    });

  } catch (err) {
    console.error("Error loading emails:", err);
    setError("Failed to load emails");
    setEmails([]);
  } finally {
    setLoading(false);
  }
};

  loadStaticData();
}, [page, rowsPerPage, searchQuery, categoryFilter, dateRange, setPage]);

  const handleDialogClose = () => {
    setSelectedEmail(null);
    setActionType(null);
    setNotes("");
  };

  const userName = localStorage.getItem("user_name");
  const handleSubmitAction = async (): Promise<void> => {
    if (!selectedEmail || actionType === null) return;

    try {
      setLoading(true);

      const emailService = new EmailApiService();
      await emailService.submitEmailAction(
        selectedEmail.id,
        actionType,
        notes,
        userName?.toString()
      );

      setEmails((prev) =>
        prev.map((email) =>
          email.id === selectedEmail.id
            ? {
                ...email,
                status: actionType === "accepted" ? "Accepted" : "Rejected",
              }
            : email
        )
      );

      // persist to localStorage so other pages / future mounts can pick it up
      try {
        const LOCAL_KEY = "emailStatusUpdates";
        const raw = localStorage.getItem(LOCAL_KEY);
        const map = raw ? JSON.parse(raw) : {};
        map[String(selectedEmail.id)] = {
          status: actionType === "accepted" ? "Accepted" : "Rejected",
          notes: notes.trim(),
          user_name: userName,
          updatedAt: new Date().toISOString(),
        };
        localStorage.setItem(LOCAL_KEY, JSON.stringify(map));
      } catch (err) {
        console.warn("Failed to persist email status update from list", err);
      }

      // dispatch event for realtime UI sync
      try {
        window.dispatchEvent(
          new CustomEvent("emailStatusChanged", {
            detail: {
              id: selectedEmail.id,
              status: actionType === "accepted" ? "Accepted" : "Rejected",
              notes: notes.trim(),
              user_name: userName,
            },
          })
        );
      } catch (err) {
        console.warn(
          "Failed to dispatch emailStatusChanged event from list",
          err
        );
      }

      setSnackbar({
        open: true,
        message: `Email #${selectedEmail.id} has been ${
          actionType === "accepted" ? "Accepted" : "Rejected"
        } successfully`,
        severity: "success",
      });
    } catch (err) {
      console.error("Error performing action:", err);
      setSnackbar({
        open: true,
        message: `Failed to mark email as ${actionType}: ${
          err instanceof Error ? err.message : "Unknown error"
        }`,
        severity: "error",
      });
    } finally {
      setLoading(false);
      handleDialogClose();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(
      (prev: {
        open: boolean;
        message: string;
        severity: "success" | "error";
      }) => ({
        ...prev,
        open: false,
      })
    );
  };

  // Filter the current page's emails locally as a fallback for customer/subject/sender search
  const currentEmails = React.useMemo(() => {
    let result = emails;
    if (hasExtensionFilter) {
      result = result.filter((email) => {
        const extraction = email.extraction;
        if (extraction && typeof extraction === "object") {
          const status = String(extraction.status || "").toLowerCase().trim();
          return status && status !== "none" && status !== "in process" && status !== "in_process";
        }
        if (extraction === null || extraction === undefined) return false;
        const value = (email.Status || email.status || email.extension || "").toString().trim().toLowerCase();
        return value && value !== "none" && value !== "in process" && value !== "in_process";
      });
    }
    const q = searchQuery.trim().toLowerCase();
    if (!q) return result;
    return result.filter((email) =>
      [email.customer_name, email.from_email, email.subject].some((field) =>
        field?.toLowerCase().includes(q)
      )
    );
  }, [emails, searchQuery, hasExtensionFilter]);

  const [selectedIds, setSelectedIds] = useState<Array<string | number>>([]);

  const allSelected = currentEmails.length > 0 && selectedIds.length === currentEmails.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentEmails.map((email) => email.id));
    }
  };

  const handleSelectOne = (id: string | number) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const handleReprocess = async () => {
    if (selectedIds.length === 0) return;

    try {
      const url = API_CONFIG.REPROCESS || `${API_CONFIG.ANSWERS_BASE_URL}/${API_ENDPOINTS.EMAILS.REPROCESS}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email_data_ids: selectedIds.map((id) => Number(id)) }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Reprocess failed with status ${response.status}`);
      }

      const data = await response.json();
      setInlineAlert({
        open: true,
        message: data?.message || `Reprocessed ${selectedIds.length} email(s)`,
        severity: 'success',
      });
      setSelectedIds([]);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Reprocess request failed';
      setInlineAlert({
        open: true,
        message: `Reprocess failed: ${message}`,
        severity: 'error',
      });
    }
  };

  const getExtractionChip = (email: any) => {
    const extraction = email.extraction;
    let raw = "";
    let count = 0;

    if (extraction && typeof extraction === "object") {
      raw = String(extraction.status || "");
      count = typeof extraction.input_file_count === "number" ? extraction.input_file_count : 0;
    } else if (extraction === null || extraction === undefined) {
      return {
        label: "None",
        isChip: true,
        dotColor: "#9e9e9e",
        bgColor: "#f5f5f5",
        textColor: "#757575",
      };
    } else {
      raw = String(email.Status || email.status || email.extension || "");
      count = Array.isArray(email.attachments) ? email.attachments.length : 0;
      if (!raw.trim() || raw.trim() === "-" || raw.trim() === "—") {
        return { label: "—", isChip: false };
      }
    }

    const lower = raw.toLowerCase().trim();

    if (!lower || lower === "none") {
      return { label: "None", isChip: true, dotColor: "#9e9e9e", bgColor: "#f5f5f5", textColor: "#757575" };
    }
    if (lower.includes("process") || lower === "pending" || lower === "in_process") {
      return { label: "Processing", isChip: true, dotColor: "#d4a373", bgColor: "#fff8e6", textColor: "#c69455" };
    }
    if (lower === "failed" || lower === "rejected") {
      return { label: "Failed", isChip: true, dotColor: "#d32f2f", bgColor: "#ffebee", textColor: "#d32f2f" };
    }
    if (lower.includes("submit")) {
      const label = raw.match(/\d/) ? raw : count > 0 ? `Submitted · ${count}` : "Submitted";
      return { label, isChip: true, dotColor: "#ffffff", bgColor: "#1a237e", textColor: "#ffffff" };
    }
    if (count > 0 || lower.includes("file")) {
      const label = lower.includes("file") ? raw : `${count} files`;
      return { label, isChip: true, dotColor: "#2e7d32", bgColor: "#e8f5e9", textColor: "#2e7d32" };
    }

    return { label: raw, isChip: true, dotColor: "#9e9e9e", bgColor: "#f5f5f5", textColor: "#757575" };
  };

  const handleExportSelected = () => {
    if (selectedIds.length === 0) return;
    const selectedRows = currentEmails.filter((email) => selectedIds.includes(email.id));
    const data = selectedRows.map((email) => ({
      "Customer Name": email.customer_name || "-",
      Subject: email.subject || "-",
      Classification: email.classification || "-",
      "From Email": email.from_email || "-",
      Received: formatDateTime(email.received_utc),
      "Run Time": email.process_time || "-",
      "Extractions": getExtractionChip(email).label,
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Selected Emails");
    XLSX.writeFile(wb, "Selected_Files_Export.xlsx");
  };

  const receivedTodayCount = React.useMemo(() => {
    return emailResponse.today_processed_count ?? 0;
  }, [emailResponse]);

  const { withExtractionsCount, failedCount, processingCount } = React.useMemo(() => {
    return {
      withExtractionsCount: emailResponse.today_completed_count ?? 0,
      failedCount: emailResponse.today_error_count ?? 0,
      processingCount: emailResponse.today_processing_count ?? 0,
    };
  }, [emailResponse]);

  const formatDateTime = (dateString: string | undefined): string => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };

  const handleEdit = async (id: number | string) => {
  try {
    const emailService = new EmailApiService();

    const response = await emailService.getQuestionById(id);

    // Check if the response contains the specific message
    if (response?.data?.message === "No answer data found for the given email") {
      setInlineAlert({
        open: true,
        message: response.data.message,
        severity: "error",
      });
      return;
    }

    window.open(`/questions/${id}`, "_blank");
  } catch (error: any) {
    console.error("Failed to fetch question data", error);
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 404) {
        setInlineAlert({
          open: true,
          message: "No answer data found for the given email.",
          severity: "error",
        });
      } else {
        setInlineAlert({
          open: true,
          message: `Failed to fetch question data: ${error.message}`,
          severity: "error",
        });
      }
    } else {
      setInlineAlert({
        open: true,
        message: "No answer data found for the given email.",
        severity: "error",
      });
    }
  }
};

  return (
    <>
      <Box
        className="main-content-area"
        sx={{
          width: isDrawerActive ? { xs: "100%", sm: "55%" } : "100%",
          transition: "width 280ms ease-in-out",
          display: "inline-block",
          verticalAlign: "top",
        }}
      >
        <Box
          sx={{
            overflow: "scroll",
            height: "calc(100vh - 71px)",
            padding: "0px",
          }}
        >
          {inlineAlert.open && (
            <MuiAlert
              severity={inlineAlert.severity}
              color={inlineAlert.severity}
              variant="filled"
              sx={{ mb: 2, mx: 2 }}
              onClose={() => setInlineAlert({ ...inlineAlert, open: false })}
            >
              {inlineAlert.message}
            </MuiAlert>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            mb:1,
            p:1
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography
                variant="h4"
                component="h1"
                className="headding-mobile"
                sx={{ fontSize: "22px", color: "#1b184d" }}
              >
                Incoming Messages
              </Typography>
            </Box>
          </Box>

          {/* Filters Section */}
          <Box sx={{ width: "100%" }}>
            <Box
              className="thin-scrollbar"
              sx={{
                marginLeft: "10px",
                marginRight: "10px",
                marginBottom:"10px",
                bgcolor: "background.paper",
                display: "flex",
                flexDirection: "row",
                flexWrap: "wrap",
                gap: 2,
                alignItems: "flex-end",
                overflowX: "auto",
                "& > *": { flexShrink: 0 },
              }}
            >
              {/* Search Field */}
              <Box
                sx={{
                  width: "100%",
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  flexWrap: "wrap",
                 
                }}
              >
                <TextField
                  variant="outlined"
                  size="small"
                  value={filters.searchQuery}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      searchQuery: e.target.value,
                    }))
                  }
                  placeholder="Search customer, subject, or sender"
                  sx={{ flex: 1, minWidth: 260 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" style={{color:"rgb(102, 102, 102)"}}  />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end" sx={{ display: filters.searchQuery ? "flex" : "none" }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              searchQuery: "",
                            }))
                          }
                          edge="end"
                        >
                          <CancelIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                    sx: {
                      bgcolor: "background.default",
                      borderRadius: "8px",
                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: "rgb(102, 102, 102)",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderWidth: 1,
                        borderColor: "primary.main",
                      },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(event) => setFilterAnchor(event.currentTarget)}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    borderColor: "divider",
                    padding:1,
                    color: "text.secondary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  Filters
                </Button>
                {/* <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {}}
                  sx={{
                    textTransform: "none",
                    borderRadius: 2,
                    padding:1,
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                  }}
                >
                  Saved views
                </Button> */}
                <Box sx={{ flexGrow: 1 }} />
                <Box
                  sx={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-start",
                    gap: 1,
                    mt: 1,
                    fontSize: "0.875rem",
                  
                  }}
                >
                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                    <Typography component="span">
                      {receivedTodayCount}
                    </Typography>
                    <Typography component="span" variant="body2">
                      received today
                    </Typography>
                  </Box>
                  <Typography component="span" sx={{ color: "text.disabled" }}>
                    ·
                  </Typography>
                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                    
                    <Typography component="span" fontWeight={700}>
                      {withExtractionsCount}
                    </Typography>
                    <Typography component="span" variant="body2" fontWeight={700}>
                      with extractions
                    </Typography>
                   
                  </Box>
                  <Typography component="span" sx={{ color: "text.disabled" }}>
                    ·
                  </Typography>
                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                    <Typography component="span" fontWeight={700}>
                      {failedCount}
                    </Typography>
                    <Typography component="span" variant="body2" fontWeight={700}>
                      failed
                    </Typography>
                  </Box>
                  <Typography component="span" sx={{ color: "text.disabled" }}>
                    ·
                  </Typography>
                  <Box component="span" display="flex" alignItems="center" gap={0.5}>
                    <Typography component="span">
                      {processingCount}
                    </Typography>
                    <Typography component="span" variant="body2">
                      still processing
                    </Typography>
                  </Box>
                </Box>
               
              </Box>

             <Popover
                open={Boolean(filterAnchor)}
                anchorEl={filterAnchor}
                onClose={() => setFilterAnchor(null)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
                PaperProps={{
                  sx: {
                    mt: 1,
                    p: 2,
                    width: 360,
                    maxWidth: "100%",
                    borderRadius: 2,
                    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                  },
                }}
              >
                {/* Category Filter */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                  sx={{ textAlign: "left" }}
                >
                  Classifications
                </Typography>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  size="small"
                  value={filters.category}
                  onChange={(e) => {
                    const value = e.target.value;
                    // If the value is an array, use it directly, otherwise convert to array
                    const selectedValues = Array.isArray(value) ? value : [value];
                    setFilters(prev => ({
                      ...prev,
                      category: selectedValues.includes('allClassifications') ? [] : selectedValues
                    }));
                  }}
                  SelectProps={{
                    multiple: true,
                     displayEmpty: true,  
                    renderValue: (selected: unknown) => {
                      const selectedValues = selected as string[];
                      return selectedValues.length === 0 
                        ? 'Select Classifications' 
                        : selectedValues.join(', ');
                    },
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 300,
                        },
                      },
                    },
                  }}
                  sx={{
                    minWidth: 200,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    color: "text.secondary",
                    "& .MuiOutlinedInput-root": {
                      bgcolor: "background.default",
                      borderRadius: 2,
                      color: "text.secondary",
                      "& .MuiSvgIcon-root": {
                        color: "#666666",
                      },
                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: "primary.main",
                      },
                    },
                    "& .MuiSelect-select": {
                      whiteSpace: 'normal',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                >
                <MenuItem value="CUSTOMER_ISSUE">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('CUSTOMER_ISSUE')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Customer Issue
                  </MenuItem>
                   <MenuItem value="INQUIRY">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('INQUIRY')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Inquiry
                  </MenuItem>
                  <MenuItem value="ORDER_CREATION">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('ORDER_CREATION')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}} // No-op, handled by parent
                    />
                    Order Creation
                  </MenuItem>

                  <MenuItem value="ORDER_UPDATE">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('ORDER_UPDATE')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Order Update
                  </MenuItem>
                  <MenuItem value="REQUEST_FOR_QUOTE">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('REQUEST_FOR_QUOTE')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Request for Quote
                  </MenuItem>
                   <MenuItem value="RMA_REQUEST">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('RMA_REQUEST')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    RMA Request
                  </MenuItem>
                  <MenuItem value="SHIPPING_NOTIFICATION">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('SHIPPING_NOTIFICATION')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Shipping Notification
                  </MenuItem>
                   <MenuItem value="SPAM">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('SPAM')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                     SPAM
                  </MenuItem>
                 
                  <MenuItem value="UNDETERMINED">
                    <input
                      type="checkbox"
                      checked={filters.category.includes('UNDETERMINED')}
                      style={{ marginRight: 8 }}
                      onChange={() => {}}
                    />
                    Undetermined
                  </MenuItem>
                  
                 
                </TextField>
              </Box>

              {/* Date Range Picker */}
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  gutterBottom
                  sx={{ textAlign: "left" }}
                >
                  Date Range
                </Typography>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    type="date"
                    size="small"
                    value={
                      filters.startDate
                        ? filters.startDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = e.target.value
                        ? new Date(e.target.value)
                        : null;
                      setFilters((prev) => ({ ...prev, startDate: date }));
                    }}
                    sx={{
                      minWidth: 140,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      color: "text.secondary",
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "background.default",
                        borderRadius: 2,
                        color: "text.secondary",
                      },
                    }}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    InputProps={{
                      sx: {
                        bgcolor: "background.default",
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mx: 0.5 }}
                  >
                    to
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={
                      filters.endDate
                        ? filters.endDate.toISOString().split("T")[0]
                        : ""
                    }
                    onChange={(e) => {
                      const date = e.target.value
                        ? new Date(e.target.value)
                        : null;
                      setFilters((prev) => ({ ...prev, endDate: date }));
                    }}
                    sx={{
                      minWidth: 140,
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 2,
                      color: "text.secondary",
                      "& .MuiOutlinedInput-root": {
                        bgcolor: "background.default",
                        borderRadius: 2,
                        color: "text.secondary",
                      },
                    }}
                    slotProps={{
                      inputLabel: {
                        shrink: true,
                      },
                      input: {
                        sx: {
                          bgcolor: "background.default",
                          borderRadius: 2,
                          "&:hover .MuiOutlinedInput-notchedOutline": {
                            borderColor: "primary.main",
                          },
                        },
                      }
                    }}
                  />
                </Box>
              </Box>
              

              {/* Action Buttons */}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  height: "100%",
                  alignItems: "flex-end",
                  mt:1
                }}
              >
               
                
                <Button
                  variant="outlined"
                  onClick={clearFilters}
                  disabled={
                    !searchQuery &&
                    categoryFilter.length === 0 &&
                    !dateRange.startDate &&
                    !dateRange.endDate 
                  }
                  size="small"
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                    borderRadius: 1,
                    fontWeight: 500,
                    borderColor: "divider",
                    color: "text.secondary",
                    "&:hover": {
                      borderColor: "text.secondary",
                      bgcolor: "action.hover",
                    },
                    "&:disabled": {
                      borderColor: "action.disabled",
                      color: "text.disabled",
                    },
                  }}
                >
                  Clear All
                </Button>
                
                <Button
                  variant="contained"
                  onClick={applyFilters}
                  disabled={
                    filters.searchQuery === searchQuery &&
                    filters.category === categoryFilter &&
                    filters.startDate === dateRange.startDate &&
                    filters.endDate === dateRange.endDate
                  }
                  size="small"
                  sx={{
                    minWidth: 100,
                    textTransform: "none",
                    borderRadius: 1,
                    fontWeight: 500,
                    boxShadow: "none",
                    bgcolor: "#1b184d",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    },
                    "&:disabled": {
                      bgcolor: "action.disabledBackground",
                      color: "text.disabled",
                    },
                  }}
                >
                  Apply
                </Button>

              </Box>
              </Popover>

              {/* {(searchQuery ||
                categoryFilter ||
                dateRange.startDate ||
                dateRange.endDate) && (
                <Chip
                  label={`${emailResponse.pagination?.total_records} result${
                    emailResponse.pagination?.total_records !== 1 ? "s" : ""
                  } found`}
                  size="small"
                  sx={{
                    bgcolor: "#1b184d",
                    color: "primary.contrastText",
                    fontWeight: 500,
                    height: 24,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                />
              )} */}
            </Box>

            {selectedIds.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  bgcolor: "#1b184d",
                  color: "white",
                  px: 2,
                  py: 1,
                  borderRadius: 1,
                  mx: 1.25,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: "white" }}
                >
                  {selectedIds.length} selected
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleReprocess}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Reprocess
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleExportSelected}
                  sx={{
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "white",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Export selected
                </Button>
                
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  onClick={() => setSelectedIds([])}
                  size="small"
                  sx={{
                    color: "white",
                    textTransform: "none",
                    "&:hover": { bgcolor: "rgba(255,255,255,0.1)" },
                  }}
                >
                  Clear selection
                </Button>
              </Box>
            )}

            <TableContainer
              sx={{
                width: "100%",
                overflow: "auto !important",
              }}
            >
              <Table
                sx={{
                  borderCollapse: "collapse",
                  "& td": {
                    borderBottom: "1px solid rgb(233 234 235)",
                    padding: "8px 16px",
                    textAlign: "left",
                  },

                  "& th": {
                    borderBottom: "2px solid rgb(233 234 235)",
                    backgroundColor: "#E9EAEB",
                    padding: "8px 16px",
                    textAlign: "left",
                  },
                  "& tr:last-child td": {
                    borderBottom: "1px solid rgb(233 234 235)",
                  },
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "background.default" }}>
                    <TableCell padding="checkbox" align="center">
                      <Checkbox
                        size="small"
                        icon={<CheckBoxUncheckedIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                        checkedIcon={<CheckBoxCheckedIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                        indeterminateIcon={<IndeterminateIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                        indeterminate={someSelected}
                        checked={allSelected}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                      Customer Name / Subject
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                      Classification
                    </TableCell>
                    
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                      From Email
                    </TableCell>
                    
                    
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                      Received
                    </TableCell>
<TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                      Run Time
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "80px",
                        maxWidth: "180px",
                        textAlign: "center",
                      }}
                    >
                     Files Extracted
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        fontWeight: 600,
                        color: "text.primary",
                        wordBreak: "break-word",
                        minWidth: "150px",
                        maxWidth: "200px",
                        textAlign: "center",
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
             <TableRow>
  <TableCell colSpan={8}>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        py: 2,
      }}
    >
      <CircularProgress
        sx={{
          mb: 1,
          color: "text.primary",
        }}
      />

      <Typography variant="body2" sx={{ color: "text.secondary" }}>
        Loading emails...
      </Typography>
    </Box>
  </TableCell>
</TableRow>
                  ) : currentEmails.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{}}>
                        <Box
                          sx={{
                            border: "1px solid",
                            borderColor: "divider",
                            borderRadius: 1,
                            bgcolor: "background.paper",
                            boxShadow: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            sx={{
                              color: "text.primary",
                              m: 1,
                              fontWeight: 500,
                              fontSize: "12px",
                              textAlign:'center',
                            }}
                          >
                            {getNoResultsMessage()}
                          </Typography>
                          
                          
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    currentEmails.map((email) => (
                      <TableRow
                        key={email.id}
                        hover
                        sx={{
                          "&:hover": {
                            bgcolor: "action.hover",
                          },
                          "&:last-child td, &:last-child th": {
                            border: 0,
                          },
                          backgroundColor:
                            drawerEmail === email.id ? "#b8b4f129" : "",
                        }}
                      >
                        <TableCell padding="checkbox" align="center">
                          <Checkbox
                            size="small"
                            icon={<CheckBoxUncheckedIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                            checkedIcon={<CheckBoxCheckedIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                            indeterminateIcon={<IndeterminateIcon fontSize="small" sx={{ color: "rgb(102, 102, 102)" }} />}
                            checked={selectedIds.includes(email.id)}
                            onChange={() => handleSelectOne(email.id)}
                          />
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: "text.primary",
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {email.customer_name || "-"} <br/>
                          <Tooltip
                            title={
                              <Box
                                sx={{
                                  maxWidth: 1000,
                                  maxHeight: 400,
                                  fontSize: "12px",
                                  width: "100%",
                                  overflow: "auto",
                                }}
                              >
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    fontWeight: 500,
                                    mb: 1,
                                    color: "white",
                                  }}
                                >
                                  Full Subject:
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "white", fontSize: "12px" }}
                                >
                                  {email.subject}
                                </Typography>

                                <Divider
                                  sx={{
                                    borderColor: "rgba(255, 255, 255, 0.2)",
                                    my: 1,
                                    fontSize: "12px",
                                  }}
                                />
                                {email.summary && (
                                  <Box sx={{ mb: 2 }}>
                                    <Typography
                                      variant="subtitle2"
                                      sx={{
                                        fontWeight: 500,
                                        mb: 1,
                                        color: "white",
                                        fontSize: "12px",
                                      }}
                                    >
                                      Summary:
                                    </Typography>
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "white",
                                        lineHeight: 1.4,
                                        fontSize: "12px",
                                      }}
                                    >
                                      {email.summary}
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            }
                            arrow
                            placement="top"
                            sx={{
                              "& .MuiTooltip-tooltip": {
                                bgcolor: "#000000",
                                color: "white",
                                fontSize: "12px",
                                border: "1px solid #333333",
                              },
                              "& .MuiTooltip-arrow": {
                                color: "#000000",
                                "&:before": {
                                  border: "1px solid #333333",
                                },
                              },
                            }}
                          >
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{
                                maxWidth: 250,
                                color: "text.primary",
                                cursor: "pointer",
                                "&:hover": {
                                  color: "primary.main",
                                },
                              }}
                            >
                              {email.subject}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center"
                          sx={{
                            color: "text.primary",
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}>
                          {email.classification || "-"}
                          </TableCell>
                      
                        <TableCell
                          align="center"
                          sx={{
                            color: "text.primary",
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {email.from_email}
                        </TableCell>
                       
                        <TableCell
                          align="center"
                          sx={{
                            color: "#3A3541",
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {formatDateTime(email.received_utc)}
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{
                            color: "#3A3541",
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {email.process_time}
                        </TableCell>

                        <TableCell
                          align="center"
                          sx={{
                            wordBreak: "break-word",
                            minWidth: "80px",
                            maxWidth: "180px",
                            fontSize: "12px",
                            textAlign: "center",
                          }}
                        >
                          {(() => {
                            const chip = getExtractionChip(email);
                            return chip.isChip ? (
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  flexDirection: { xs: "column", md: "row" },
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: { xs: 0.25, md: 0.75 },
                                  px: 1.25,
                                  py: 0.5,
                                  borderRadius: 10,
                                  bgcolor: chip.bgColor,
                                  color: chip.textColor,
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    flexShrink: 0,
                                    borderRadius: "50%",
                                    bgcolor: chip.dotColor,
                                  }}
                                />
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 500,
                                    whiteSpace: "nowrap",
                                    textAlign: "center",
                                  }}
                                >
                                  {chip.label}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography
                                component="span"
                                sx={{ color: "text.secondary", fontSize: "12px" }}
                              >
                                —
                              </Typography>
                            );
                          })()}
                        </TableCell>

                        <TableCell
                          align="left"
                          sx={{
                            wordBreak: "break-word",
                            minWidth: "150px",
                            maxWidth: "200px",
                            py: 1.5,
                            textAlign: "left",

                            "& .MuiIconButton-root": {
                              margin: "0 4px",
                            },
                            "& > div": {
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "flex-start",
                              flexWrap: "nowrap",
                              whiteSpace: "nowrap",
                              width: "100%",
                            },
                          }}
                        >
                          <div>
                           <Tooltip title="Edit Questionnaires">
  <IconButton
    size="small"
    // onClick={() => {
    //   window.open(`/questions?id=${email.id}`, "_blank");
    // }}
    onClick={() => handleEdit(email.id)}
  >
    <EditIcon
      sx={{ fontSize: 18, color: "#1b184d" }}
    />
  </IconButton>
</Tooltip>
                           
                            <Tooltip title="Send Email">
                              <IconButton
                                size="small" 
                              >
                                <SendIcon
                                  sx={{ fontSize: 18, color: "#1b184d" }}
                                />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Open details">
                              <IconButton
                                size="small"
                                onClick={() => openDrawerForEmail(email.id)}
                                sx={{
                                  width: 25,
                                  height: 25,
                                  color: "#1b184d",
                                  border: "1px solid #1b184d",
                                  borderRadius: "50%",
                                }}
                                aria-label={`open-details-${email.id}`}
                              >
                                <ArrowForwardIcon
                                  sx={{ fontSize: 15, color: "#1b184d" }}
                                />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Enhanced Pagination */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mt={2}
              pl={2}
              gap={2}
            >
              <Box display="flex" alignItems="center">
                <Typography variant="body2" color="text.secondary" mr={1}>
                  Rows per page:
                </Typography>
                <Select
                  value={rowsPerPage}
                  onChange={handleChangeRowsPerPage as any}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: "32px",
                    border: "1px solid #808080",
                    "& .MuiSelect-select": {
                      py: "6px",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#808080", // light gray
                    },
                    mr: 2,
                  }}
                >
                  {[5, 10, 25, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="body2" color="text.secondary">
                  {`${(page - 1) * rowsPerPage + 1}-${Math.min(
                    page * rowsPerPage,
                    emailResponse.pagination?.total_records || 0
                  )} of ${emailResponse.pagination?.total_records || 0}`}
                </Typography>
              </Box>

              <Box display="flex" alignItems="center" gap={1}>
                <IconButton
                  onClick={handleFirstPage}
                  disabled={page === 1}
                  size="small"
                  sx={{
                    "&:disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <FirstPage sx={{ color: "#3A3541" }} />
                </IconButton>

                <IconButton
                  onClick={handlePreviousPage}
                  disabled={page === 1}
                  size="small"
                  sx={{
                    "&:disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <ChevronLeft sx={{ color: "#3A3541" }} />
                </IconButton>

                <PaginationButtons
                  currentPage={page}
                  totalRecords={emailResponse.pagination?.total_records || 0}
                  rowsPerPage={rowsPerPage}
                  onPageChange={handlePageNumberClick}
                  disabled={loading}
                />

                <IconButton
                  onClick={handleNextPage}
                  disabled={
                    page >=
                    Math.ceil(
                      (emailResponse.pagination?.total_records || 0) /
                        rowsPerPage
                    )
                  }
                  size="small"
                  sx={{
                    "&:disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <ChevronRight sx={{ color: "#3A3541" }} />
                </IconButton>

                <IconButton
                  onClick={handleLastPage}
                  disabled={
                    page >=
                    Math.ceil(
                      (emailResponse.pagination?.total_records || 0) /
                        rowsPerPage
                    )
                  }
                  size="small"
                  sx={{
                    "&:disabled": {
                      color: "text.disabled",
                    },
                  }}
                >
                  <LastPage sx={{ color: "#3A3541" }} />
                </IconButton>
              </Box>
            </Box>
          </Box>
         

        </Box>
      </Box>

      {/* Right-side full-height Drawer */}
      {/* Right-side fixed panel (no backdrop) */}
      {drawerOpen && (
        <Box
          className="right-side-panel thin-scrollbar"
          sx={{
            position: "fixed",
            right: 0,
            top: '66px',
            bottom: 0,
            padding: 2,
            width: { xs: "100%", sm: "45%" },
            maxWidth: 720,
            backgroundColor: "#b8b4f129",
            borderLeft: "1px solid rgba(0,0,0,0.08)",
            boxShadow: "-2px 0 12px rgba(0,0,0,0.06)",
            zIndex: 1400,
            display: "flex",
            flexDirection: "column",
            overflow: "auto",
            transform: "translateX(0)",
            transition: "transform 280ms ease-in-out, width 280ms ease-in-out",
          }}
        >
          <EmailDetailsViewer
            onClose={closeDrawer}
            emailId={drawerEmail || ""}
          />
        </Box>
      )}

      {/* Action Confirmation Dialog */}
      <Dialog
        open={!!selectedEmail}
        onClose={handleDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {actionType === "accepted" ? "Mark as Accepted" : "Mark as Rejected"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            autoComplete="off"
            margin="dense"
            label="Notes"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={`Add notes for ${actionType || ""} action...`}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "5px",
                "& fieldset": {
                  border: "1px solid #3A3541",
                },
                "&:hover fieldset": {
                  border: "1px solid #3A3541", // prevents thicker/darker hover border
                },
                "&.Mui-focused fieldset": {
                  border: "1px solid #3A3541", // prevents blue border on focus
                },
              },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAction}
            color={actionType === "accepted" ? "success" : "error"}
            variant="contained"
            disabled={loading || notes.trim().length === 0}
            sx={{
              "&.Mui-disabled": {
                backgroundColor: "#e0e0e0", // gray background
                color: "#9e9e9e", // gray text
              },
            }}
          >
            {loading
              ? "Processing..."
              : actionType === "accepted"
              ? "Mark as Accepted"
              : "Mark as Rejected"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
          elevation={6}
          variant="filled"
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>
    </>
  );
};

export default EmailAttachmentsPage;
